/**
 * CORS Proxy Utilities for WMS Layers
 *
 * This module provides utilities for handling CORS issues with external WMS servers.
 *
 * Proxy usage is controlled by the `useProxy` setting on each WMS service:
 * - When adding a new WMS: getCapabilities() tries direct fetch first, falls back to proxy
 *   on CORS error, and returns `useProxy: true` which gets saved to settings.
 * - When loading tiles: Only uses proxy if `useProxy: true` is set in the WMS settings.
 *   If a CORS error occurs at runtime, the individual layer's source URL is updated to
 *   use the proxy, but this does NOT affect other layers.
 *
 * Note: The `corsProxyRequiredOrigins` Set is no longer used to share CORS state between
 * different WMS layers. This prevents one layer's CORS requirements from forcing all other
 * layers from the same origin to use the proxy unnecessarily.
 */

// Legacy: This Set is no longer used but kept for compatibility
export const corsProxyRequiredOrigins = new Set<string>();

/** Convert a URL to use the CORS proxy */
export const getProxyUrl = (originalUrl: string): string => {
  return `/wms-proxy/${encodeURIComponent(originalUrl)}`;
};

/** Check if a URL is external (different origin than our app) */
export const isExternalUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.origin !== window.location.origin;
  } catch {
    return false;
  }
};

/** Get the origin from a URL for tracking CORS requirements */
export const getUrlOrigin = (url: string): string => {
  try {
    return new URL(url).origin;
  } catch {
    return url;
  }
};

/** Check if a URL is already using the proxy */
export const isProxyUrl = (url: string): boolean => {
  return url.startsWith('/wms-proxy/');
};

/** Extract the original URL from a proxy URL */
export const getOriginalUrlFromProxy = (proxyUrl: string): string => {
  if (!isProxyUrl(proxyUrl)) return proxyUrl;
  const encoded = proxyUrl.replace('/wms-proxy/', '');
  return decodeURIComponent(encoded);
};
